import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { useStrings } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { getRecommendation } from '../services/recommendService';

const QUESTION_KEYS = ['skinType', 'climate', 'concern', 'texture', 'fragrance'];

export default function Quiz() {
  const strings = useStrings();
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const t = strings.quiz;

  // Feature 11: Redirect if not enabled
  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_AI_QUIZ !== 'true') {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const [step, setStep] = useState(0); // 0-indexed
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentKey = QUESTION_KEYS[step];
  const currentQuestion = t.questions[currentKey];
  const optionKeys = Object.keys(currentQuestion.options);
  const totalSteps = QUESTION_KEYS.length;
  const isLastStep = step === totalSteps - 1;
  const selectedOption = answers[currentKey] || null;

  const handleSelect = (optionKey) => {
    setAnswers((prev) => ({ ...prev, [currentKey]: optionKey }));
  };

  const handleNext = () => {
    if (!selectedOption) return;
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;
    setLoading(true);
    setError(null);
    try {
      const idToken = await getIdToken();
      const result = await getRecommendation(answers, idToken);
      navigate('/quiz/result', { state: { result } });
    } catch (err) {
      console.error('[Quiz]', err.message);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const progressPct = ((step) / totalSteps) * 100;

  return (
    <div className="min-h-[80vh] w-full flex flex-col items-center justify-center px-4 py-12 md:py-20">

      {/* Card */}
      <div className="w-full max-w-xl bg-surface rounded-3xl shadow-card p-6 md:p-10 fade-section show">

        {/* Step counter */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-textSecondary font-sans">
            {t.step} {step + 1} {t.of} {totalSteps}
          </span>
          <span className="text-xs text-textSecondary font-sans">
            {Math.round(((step + 1) / totalSteps) * 100)}%
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-cardBg rounded-full mb-8 overflow-hidden">
          <div
            className="h-full bg-secondary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPct + (1 / totalSteps) * 100}%` }}
          />
        </div>

        {/* Question */}
        <h2 className="text-xl md:text-2xl font-heading text-headingText mb-6 leading-snug">
          {currentQuestion.label}
        </h2>

        {/* Options */}
        <div className="flex flex-col gap-3 mb-8">
          {optionKeys.map((key) => (
            <button
              key={key}
              onClick={() => handleSelect(key)}
              className={`w-full text-left px-4 py-3 md:py-3.5 rounded-xl border-2 text-sm md:text-base font-sans transition-all duration-200
                ${selectedOption === key
                  ? 'border-secondary bg-secondary/10 text-headingText font-semibold'
                  : 'border-cardBg bg-cardBg text-bodyText hover:border-secondary/50 hover:bg-secondary/5'
                }`}
            >
              {currentQuestion.options[key]}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm text-center mb-4">{error}</p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          {step > 0 ? (
            <PanchTatvaButton variant="ghost" onClick={handleBack} disabled={loading}>
              ← {t.back}
            </PanchTatvaButton>
          ) : (
            <div />
          )}

          {isLastStep ? (
            <PanchTatvaButton
              onClick={handleSubmit}
              disabled={!selectedOption || loading}
              className={!selectedOption ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {loading ? t.loading : t.submit}
            </PanchTatvaButton>
          ) : (
            <PanchTatvaButton
              onClick={handleNext}
              disabled={!selectedOption}
              className={!selectedOption ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {t.next} →
            </PanchTatvaButton>
          )}
        </div>
      </div>

      {/* Decorative blobs */}
      <div className="absolute top-20 left-8 w-3 h-3 bg-secondary rounded-full animate-floatSlow opacity-40 pointer-events-none" />
      <div className="absolute bottom-24 right-12 w-5 h-5 bg-secondary rounded-full animate-floatSlow opacity-30 pointer-events-none" />
    </div>
  );
}
